import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';

import { StripeService } from './stripe.service';
import { UserProfile } from '../models/user-profile.model';
import { PaymentProviderType } from '../models/payment-provider/user-payment-provider';
import { MangopayService } from './mangopay.service';
import { MangopayCardRegistrationData } from '../models/payment-provider/mangopay.model';
import { MangopayFormData } from '../shared/components/payment-form/mangopay-payment-form/mangopay-payment-form.component';
import { StripeFormData } from '../shared/components/payment-form/stripe-payment-form/stripe-payment-form.component';


@Injectable({
  providedIn: 'root',
})
export class PaymentProviderService {
  private stripeService = inject(StripeService);
  private mangopayService = inject(MangopayService);

  public upsertUserToken(user: Partial<UserProfile>, paymentProviderRequired: PaymentProviderType[]) {
    const observableReq = paymentProviderRequired.reduce( (obs, upp) => {
      switch (upp) {
      case PaymentProviderType.Stripe:
        return {
          ...obs,
          [PaymentProviderType.Stripe]: this.upsertUserTokenStripe(user),
        };
      default:
        return obs;
      }
    }, {default: of('forkjoin requires at least one obs')});
    return forkJoin(observableReq,
    ).pipe(
      map( (ppTokens) => ppTokens),
    );

  }

  /**
   * Sends payment provider CB data
   * Should be used before sending purchase data
   */
  public paymentCardRegistrationBeforeSave(
    provider: PaymentProviderType,
    paymentIntent: {},
    providerFormData: MangopayFormData | StripeFormData,
  ): Observable<{
    [PaymentProviderType.Mangopay]: {
      RegistrationData: unknown,
      BrowserInfo: unknown,
      Id: unknown,
    }
  } | { [PaymentProviderType.Stripe]: unknown}> {
    switch (provider) {
    case PaymentProviderType.Mangopay:
      if (providerFormData.provider !== provider) {
        throw new Error('provider form data does not match');
      }
      return this.mangopayService.createCardRegistration(
        paymentIntent as MangopayCardRegistrationData,
        providerFormData.cardData,
      );
    case PaymentProviderType.Stripe:
      // stripe does its own card registration
      return of({[PaymentProviderType.Stripe]: 'before save'});
    default:
      throw new Error('no paymentDirectPurchase implemented for provider');
    }
  }


  /**
   * Sends payment provider CB data
   * Should be used after sending purchase data
   */
  public paymentCardRegistrationAfterSave(
    provider: PaymentProviderType,
    paymentIntent: {},
    providerFormData: MangopayFormData | StripeFormData,
    secureReturnUrl: string,
  ) {

    switch (provider) {
    case PaymentProviderType.Mangopay:
      return Promise.resolve({[PaymentProviderType.Mangopay]: undefined});
    case PaymentProviderType.Stripe:
      if (providerFormData.provider !== provider) {
        throw new Error('provider form data does not match');
      }
      return this.stripeService.confirmPayment(
        providerFormData.elements,
        secureReturnUrl,
        providerFormData.purchase,
      ).then(
        (r) => ({[PaymentProviderType.Stripe]: r}),
      );
    default:
      throw new Error('no paymentDirectPurchase implemented for provider');
    }
  }

  private upsertUserTokenStripe(user: Partial<UserProfile>) {
    return this.stripeService.createUser(user);
  }

}
